import FilterButton from '@components/FilterV3/components/FilterButton/FilterButton';
import ToolbarPopover from '@components/FilterV3/components/ToolbarPopover/ToolbarPopover';
import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack, Theme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Grid2x2 } from 'lucide-react';
import { useState } from 'react';
import LangEnum from '../../../../../../lang/LangEnum';
import { useUpdateCatalogViewPreferences } from '../../../../../../services/modules/catalogs/catalogs.mutations';
import { useGetCatalog } from '../../../../../../services/modules/catalogs/catalogs.queries';
import { useUpdateOrCreateViewPreference } from '../../../../../../services/modules/userContextPreferences/userContextPreferences.mutations';
import { ViewPreferenceRecognizedLists } from '../../../../../../services/modules/userContextPreferences/userContextPreferences.types';
import { CatalogProductsTestIds } from '../../../../../../utils/testIds/CatalogProducts.testIds';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { useCatalogView } from '../../contexts/CatalogView.context';
import { CatalogViewCatalogFields, CatalogViewMode } from '../../view/catalogView.types';
import { isFreezingArrangement } from '../../view/isFreezingArrangement';
import { mapDraftToPatchDto } from '../../view/mapDraftToPatchDto';
import { toCatalogViewCatalogFields } from '../../view/resolveEffectiveView';
import { resolveNotGroupedWarning } from '../../view/resolveNotGroupedWarning';
import CatalogViewSettingsContent from './CatalogViewSettingsContent';
import {
  CatalogViewSettingsBodyProps,
  CatalogViewSettingsContentEnablement,
  CatalogViewSettingsContentVisibility,
  CatalogViewSettingsProps,
} from './catalogViewSettings.types';
import CatalogViewFreezeArrangementChoice from './components/CatalogViewFreezeArrangementChoice';
import { useCatalogViewDraft } from './hooks/useCatalogViewDraft';
import { resolvePreferenceWrite } from './resolvePreferenceWrite';

const POPOVER_PAPER_SX = (theme: Theme) => ({
  width: 'min(240px, calc(100vw - 32px))',
  padding: theme.spacing(1, 1.5),
});

const CatalogViewSettings = (props: CatalogViewSettingsProps) => {
  const { translate } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { mutate: savePreference, isPending: isSavingPreference } =
    useUpdateOrCreateViewPreference();
  const { mutate: saveCatalogView, isPending: isSavingCatalogView } =
    useUpdateCatalogViewPreferences({ catalogId: props.catalogId });

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <FilterButton
        label={translate(LangEnum.GLOBAL_VIEW)}
        icon={Grid2x2}
        testId={CatalogProductsTestIds.VIEW_BUTTON}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      />
      <ToolbarPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        testId={CatalogProductsTestIds.VIEW_POPOVER}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        paperSx={POPOVER_PAPER_SX}
      >
        <CatalogViewSettingsBody
          {...props}
          preferenceMutation={{ save: savePreference, isSaving: isSavingPreference }}
          catalogViewMutation={{ save: saveCatalogView, isSaving: isSavingCatalogView }}
          onClose={handleClose}
        />
      </ToolbarPopover>
    </>
  );
};

const CatalogViewSettingsBody = ({
  catalogId,
  canEditCatalogConfig,
  canPersist,
  catalogConfig,
  isManualGroupingCriterion,
  hasSavedUserPreference,
  isCatalogSettled,
  isPreferencesSettled,
  isOfflineMode,
  preferenceMutation,
  catalogViewMutation,
  onClose,
}: CatalogViewSettingsBodyProps) => {
  const { translate } = useTranslation();
  const { queryKey } = useGetCatalog({ id: catalogId, isOfflineMode }, { enabled: !isOfflineMode });
  const queryClient = useQueryClient();
  const { applied, setApplied } = useCatalogView();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [shouldFreezeCurrentArrangement, setShouldFreezeCurrentArrangement] = useState(false);

  const {
    draft,
    configSnapshot,
    dirtyForMe,
    dirtyForAll,
    isCombinationInvalid,
    isSortTargetMissing,
    isGroupTargetMissing,
    canApplyForMe,
    canApplyForAll,
    canRestore,
    restore,
    setField,
  } = useCatalogViewDraft({
    applied,
    catalogConfigLive: catalogConfig,
    hasSavedUserPreference,
    isPreferencesSettled,
    isCatalogSettled,
    isApplyForAllEnabled: canEditCatalogConfig,
  });

  const isWritingForAll = catalogViewMutation.isSaving;

  const isConfirmRequired = isFreezingArrangement({ configSnapshot, dirtyForAll, draft });

  const applyForMe = (baseline: CatalogViewCatalogFields) => {
    if (!draft) {
      return;
    }

    const preferenceWrite = canPersist
      ? resolvePreferenceWrite({
          draft,
          configSnapshot: baseline,
          dirtyForMe,
          hasSavedUserPreference,
        })
      : null;

    if (preferenceWrite) {
      preferenceMutation.save(preferenceWrite);
    }

    setApplied({
      mode: draft.mode,
      cardSize: draft.cardSize,
      groupBy: draft.groupBy,
      groupTargetId: draft.groupTargetId,
      sortCriterion: draft.sortCriterion,
      sortTargetId: draft.sortTargetId,
    });
  };

  const handleClearPreferences = () => {
    if (!configSnapshot) {
      return;
    }

    setApplied({ ...configSnapshot });
    restore();

    if (!isPreferencesSettled) {
      return;
    }

    preferenceMutation.save({
      list: ViewPreferenceRecognizedLists.CATALOG_PRODUCTS,
      defaultView: null,
    });
  };

  const handleApplyForMe = () => {
    if (!configSnapshot) {
      return;
    }

    applyForMe(configSnapshot);
    onClose();
  };

  const applyForAll = () => {
    if (!draft) {
      return;
    }

    const patch = mapDraftToPatchDto(dirtyForAll, draft);
    const payload = isConfirmRequired ? { ...patch, shouldFreezeCurrentArrangement } : patch;

    catalogViewMutation.save(payload, {
      onSuccess: (catalog) => {
        setIsConfirmOpen(false);
        applyForMe(toCatalogViewCatalogFields(catalog));
        queryClient.setQueryData(queryKey, catalog);
        onClose();
      },
    });
  };

  const handleApplyForAll = () => {
    if (isConfirmRequired) {
      setShouldFreezeCurrentArrangement(false);
      setIsConfirmOpen(true);
      return;
    }

    applyForAll();
  };

  const notGroupedWarning = resolveNotGroupedWarning({
    catalogConfig,
    isManualGroupingCriterion,
    isPending: isWritingForAll,
  });

  const visibility: CatalogViewSettingsContentVisibility = {
    showGroupConfig: canEditCatalogConfig,
    showCatalogConfig: canEditCatalogConfig,
    showSortConfig: canEditCatalogConfig,
    showCardSizeConfig: draft?.mode === CatalogViewMode.CARD,
  };

  const enablement: CatalogViewSettingsContentEnablement = {
    isCombinationInvalid,
    isSortTargetMissing,
    isGroupTargetMissing,
    canApplyForMe: canApplyForMe && !preferenceMutation.isSaving,
    canApplyForAll: canApplyForAll && !catalogViewMutation.isSaving,
    canRestore,
  };

  return (
    <>
      <CatalogViewSettingsContent
        draft={draft}
        isOfflineMode={isOfflineMode}
        notGroupedWarning={notGroupedWarning}
        visibility={visibility}
        enablement={enablement}
        onModeChange={(mode) => setField('mode', mode)}
        onGroupByChange={(groupBy) => setField('groupBy', groupBy)}
        onGroupTargetIdChange={(groupTargetId) => setField('groupTargetId', groupTargetId)}
        onSortCriterionChange={(sortCriterion) => setField('sortCriterion', sortCriterion)}
        onSortTargetIdChange={(sortTargetId) => setField('sortTargetId', sortTargetId)}
        onCardSizeChange={(cardSize) => setField('cardSize', cardSize)}
        onRestore={handleClearPreferences}
        onApplyForMe={handleApplyForMe}
        onApplyForAll={handleApplyForAll}
      />
      <Modal
        isOpen={isConfirmOpen}
        size="standard"
        header={{ title: LangEnum.CATALOGS_VIEW_MANUAL_ARRANGEMENT_CONFIRM_TITLE }}
        onClose={() => setIsConfirmOpen(false)}
        footer={{
          primaryAction: {
            label: LangEnum.GLOBAL_CONFIRM_LABEL,
            isLoading: isWritingForAll,
            onClick: applyForAll,
          },
          secondaryAction: {
            label: LangEnum.GLOBAL_CANCEL_LABEL,
            onClick: () => setIsConfirmOpen(false),
          },
        }}
      >
        <Stack spacing={1.5}>
          <CatalogViewFreezeArrangementChoice
            isFreezingArrangement={shouldFreezeCurrentArrangement}
            onChange={setShouldFreezeCurrentArrangement}
          />
          {shouldFreezeCurrentArrangement && (
            <Typography
              variant="body2"
              testId={CatalogProductsTestIds.VIEW_MANUAL_ARRANGEMENT_CONFIRM}
            >
              {translate(LangEnum.CATALOGS_VIEW_MANUAL_ARRANGEMENT_CONFIRM_MESSAGE)}
            </Typography>
          )}
        </Stack>
      </Modal>
    </>
  );
};

export default CatalogViewSettings;
